import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { Text } from "@fluentui/react";
import type { Email, FTLShipment } from "@src/types/api";
import { getRatingEngineBranding } from "../providerBranding";
import { SonarRateCard } from "./SonarRateCard";
import { formatUsd, parseFiniteNumber } from "./rateDisplayFormat";

type MarketRateRow = NonNullable<FTLShipment["marketRates"]>[number];
type RatingEngineProviderInfo = NonNullable<Email["providerSetup"]>["ratingEngines"][number];

function marketRateValue(row: MarketRateRow): number | null {
  const rates = row?.rates;

  if (!rates || typeof rates !== "object") return null;
  const rec = rates as Record<string, unknown>;
  const candidates = [
    rec.totalBilledRate,
    rec.totalRate,
    rec.price,
    rec.rate,
    rec.cost,
    rec.suggestedCost,
    rec.tmsRate,
    rec.tmsCost,
    rec.billed,
    rec.targetBuyRate,
    rec.startBuyRate,
  ];

  for (const candidate of candidates) {
    const n = parseFiniteNumber(candidate);

    if (n != null) return n;
  }
  return null;
}

function normalizeKey(v: string | undefined): string {
  return (v ?? "").trim().toLowerCase();
}

function resolveProviderInfo(
  source: string | undefined,
  ratingEngineProviders: RatingEngineProviderInfo[]
): RatingEngineProviderInfo | null {
  const sourceKey = normalizeKey(source);

  if (!sourceKey) return null;
  return (
    ratingEngineProviders.find(
      (p) => normalizeKey(p.id) === sourceKey || normalizeKey(p.name) === sourceKey
    ) ?? null
  );
}

function resolveDisplayName(
  source: string | undefined,
  ratingEngineProviders: RatingEngineProviderInfo[]
): string {
  const found = resolveProviderInfo(source, ratingEngineProviders);

  if (found) return found.name;
  const sourceKey = normalizeKey(source);

  if (!sourceKey) return "Rating Engine";
  return source ?? "Rating Engine";
}

export type EngineRendererProps = {
  row: MarketRateRow;
  displayName: string;
  valueText: string;
  providerKey: string;
  providerBranding?: RatingEngineProviderInfo["branding"];
};

function DefaultEngineRateCard({
  displayName,
  valueText,
  providerKey,
  providerBranding,
}: EngineRendererProps) {
  const branding = getRatingEngineBranding(displayName, providerBranding, providerKey);

  return (
    <Box sx={styles.engineRateCard}>
      <Stack direction="column" sx={styles.engineRateStack}>
        {branding.logoSrc ? (
          <img
            src={branding.logoSrc}
            alt={branding.logoAlt}
            style={{
              height: `${branding.logoHeight}px`,
              width: "auto",
              objectFit: "contain",
              marginBottom: "6px",
              display: "block",
              alignSelf: "flex-start",
            }}
          />
        ) : (
          <Text styles={styles.engineRateSource}>{displayName}</Text>
        )}
        <Text styles={styles.engineRateLabel}>Rate</Text>
        <Text styles={styles.engineRateValue}>{valueText}</Text>
      </Stack>
    </Box>
  );
}

function GreenscreensRateCard(props: EngineRendererProps) {
  return <DefaultEngineRateCard {...props} />;
}

const ENGINE_RENDERERS: Record<string, Function> = {
  greenscreens: GreenscreensRateCard,
  sonar: SonarRateCard,
};

function renderEngineRow(
  row: MarketRateRow,
  idx: number,
  ratingEngineProviders: RatingEngineProviderInfo[]
): React.ReactElement {
  const source =
    typeof row.source === "string" && row.source.trim() ? row.source.trim() : "rating-engine";
  const providerInfo = resolveProviderInfo(source, ratingEngineProviders);
  const displayName = resolveDisplayName(source, ratingEngineProviders);
  const value = marketRateValue(row);
  const valueText = value != null ? formatUsd(value) : "N/A";
  const renderer = ENGINE_RENDERERS[normalizeKey(source)] ?? DefaultEngineRateCard;

  return (
    <Box key={`${source}-${idx}`}>
      {renderer({
        row,
        displayName,
        valueText,
        providerKey: normalizeKey(source),
        providerBranding: providerInfo?.branding,
      })}
    </Box>
  );
}

interface RatingEnginesSectionProps {
  marketRates?: FTLShipment["marketRates"];
  ratingEngineProviders?: RatingEngineProviderInfo[];
}

export default function RatingEnginesSection({
  marketRates,
  ratingEngineProviders = [],
}: RatingEnginesSectionProps) {
  const rows = (marketRates ?? []).filter(
    (row): row is MarketRateRow => !!row && typeof row === "object"
  );

  return (
    <Box sx={styles.offerSection}>
      <Text variant="medium" styles={styles.sectionTitle}>
        Rating Engines
      </Text>
      <Box sx={styles.engineRatesGrid}>
        {rows.map((row, idx) => renderEngineRow(row, idx, ratingEngineProviders))}
        {rows.length === 0 && (
          <Text styles={styles.emptySectionText}>No rating engine results yet.</Text>
        )}
      </Box>
    </Box>
  );
}

const styles = {
  offerSection: {
    marginTop: "20px",
  },
  sectionTitle: {
    root: {
      fontSize: "13px",
      fontWeight: "600",
      color: "#323130",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "8px",
    },
  },
  engineRatesGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  engineRateCard: {
    border: "1px solid #e1e1e1",
    borderRadius: "8px",
    padding: "12px 16px",
    backgroundColor: "#ffffff",
  },
  engineRateStack: {
    gap: "2px",
  },
  engineRateSource: {
    root: {
      fontSize: "14px",
      color: "#323130",
      fontWeight: "600",
      marginBottom: "4px",
      textTransform: "uppercase",
    },
  },
  engineRateLabel: {
    root: {
      fontSize: "11px",
      color: "#605e5c",
      marginBottom: "2px",
      textTransform: "uppercase",
    },
  },
  engineRateValue: {
    root: {
      fontSize: "18px",
      color: "#323130",
      fontWeight: "700",
      lineHeight: "24px",
    },
  },
  emptySectionText: {
    root: {
      fontSize: "12px",
      color: "#605e5c",
      padding: "4px 0",
    },
  },
};
